"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"

// Configuration: Set to true for large badges (Icon + Text), false for small (Icon only)
const USE_LARGE_BADGES = true // TODO: Make this dynamic via props if needed later

interface ModalityInfo {
    icon: React.ReactNode
    name: string
    description: string
    bgColorClass: string
    textColorClass: string
}

// Define modalities: icons, names, descriptions, and colors
// Using different chart colors and adjusting brightness/saturation for better contrast
const modalityMap: Record<string, ModalityInfo> = {
    text: {
        icon: <span className="font-bold text-xs">Aa</span>,
        name: "Text",
        description: "Processes or generates textual content.",
        // Using Chart 5 (Pink/Red base)
        bgColorClass: "bg-[hsl(var(--chart-5))]/20 dark:bg-[hsl(var(--chart-5))]/15",
        textColorClass: "text-[hsl(var(--chart-5))] dark:text-[hsl(var(--chart-5))]/90",
    },
    image: {
        icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2.5" />
            </svg>
        ),
        name: "Image",
        description: "Processes or generates images.",
        // Using Chart 3 (Orange/Yellow base)
        bgColorClass: "bg-[hsl(var(--chart-3))]/20 dark:bg-[hsl(var(--chart-3))]/15",
        textColorClass: "text-[hsl(var(--chart-3))] dark:text-[hsl(var(--chart-3))]/90",
    },
    search: {
        icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2.5" />
                <path d="M21 21L17 17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
        name: "Search",
        description: "Accesses external information or performs web searches.",
        // Using Chart 1 (Blue base)
        bgColorClass: "bg-[hsl(var(--chart-1))]/20 dark:bg-[hsl(var(--chart-1))]/10", // Kept blue as it's common for search
        textColorClass: "text-[hsl(var(--chart-1))] dark:text-[hsl(var(--chart-1))]/90",
    },
    video: {
        icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2.5" />
                <path d="M10 12L15 9V15L10 12Z" fill="currentColor" />
            </svg>
        ),
        name: "Video",
        description: "Processes or generates video content.",
        // Using Chart 4 (Purple base)
        bgColorClass: "bg-[hsl(var(--chart-4))]/20 dark:bg-[hsl(var(--chart-4))]/15",
        textColorClass: "text-[hsl(var(--chart-4))] dark:text-[hsl(var(--chart-4))]/90",
    },
    // Add more modalities as needed (e.g., audio, code)
    // Audio Example:
    // audio: {
    //     icon: <svg>...</svg>,
    //     name: "Audio",
    //     description: "Processes or generates audio content.",
    //     // Using Chart 2 (Teal/Green base)
    //     bgColorClass: "bg-[hsl(var(--chart-2))]/20 dark:bg-[hsl(var(--chart-2))]/10",
    //     textColorClass: "text-[hsl(var(--chart-2))] dark:text-[hsl(var(--chart-2))]/90",
    // },
    default: {
        icon: <span className="font-bold text-xs">?</span>,
        name: "Unknown",
        description: "Unknown or unspecified modality.",
        bgColorClass: "bg-muted",
        textColorClass: "text-muted-foreground",
    },
}

interface ModalityBadgeProps {
    type: string
    size?: "small" | "large"
}

const ModalityBadge: React.FC<ModalityBadgeProps> = ({ type, size = USE_LARGE_BADGES ? "large" : "small" }) => {
    const modality = modalityMap[type.toLowerCase()] || modalityMap.default
    const isLarge = size === "large"

    const badgeContent = (
        <div
            className={cn(
                "flex items-center justify-center rounded-md gap-1.5",
                isLarge ? "px-2 py-1" : "h-6 w-6", // Padding for large, fixed size for small
                modality.bgColorClass,
                modality.textColorClass
            )}
        >
            <span className={cn(isLarge ? "h-4 w-4" : "h-5 w-5", "flex items-center justify-center")}>{modality.icon}</span>
            {isLarge && <span className="text-xs font-medium">{modality.name}</span>}
        </div>
    )

    return (
        <Popover>
            <PopoverTrigger asChild>
                {badgeContent}
            </PopoverTrigger>
            <PopoverContent className="w-auto text-sm p-2">
                {modality.description}
            </PopoverContent>
        </Popover>
    )
}


interface ModalityBadgesProps {
    types: string[]
    size?: "small" | "large"
    className?: string
    badgeClassName?: string
}

export const ModalityBadges: React.FC<ModalityBadgesProps> = ({
    types,
    size = USE_LARGE_BADGES ? "large" : "small",
    className,
    badgeClassName
}) => {
    return (
        <div className={cn("flex flex-wrap gap-2", className)}>
            {types.map((type, index) => (
                <ModalityBadge key={`${type}-${index}`} type={type} size={size} />
            ))}
        </div>
    )
} 